import { createReference, MedplumClient, ProfileResource } from "@medplum/core";

import { HealthGorillaNotifiableStatuses } from "@/health-gorilla/types/labs";
import { COMMUNICATION_INBOX_CATEGORY } from "@/messages/constants/messages";
import { fetchUnreadCommunications } from "@/messages/services/chat";
import { getOrganizationReference, getResourcesFromBundle } from "@/utils/medplum";

export async function fetchUnreadNotificationsCount(
  medplum: MedplumClient,
  profile: ProfileResource,
  practiceId: string | undefined,
) {
  if (!profile) return 0;
  const profileReference = createReference(profile);

  const { bundle } = await medplum.searchResources(
    "Communication",
    {
      category: COMMUNICATION_INBOX_CATEGORY,
      status: "in-progress",
      recipient: profileReference.reference,
      _compartment: getOrganizationReference(practiceId),
    },
    { cache: "reload" },
  );
  const communications = getResourcesFromBundle("Communication", bundle);
  let unreadMessagesCount = 0;

  for (const communication of communications) {
    const unreadCommunications = await fetchUnreadCommunications(medplum, profile, communication, {
      _count: 10000,
    });

    unreadMessagesCount += unreadCommunications.length;

    if (unreadMessagesCount >= 100) {
      // We only show up to 99+ unread notifications in the navbar,
      // so if we already have 100, there's no need to continue
      break;
    }
  }
  return unreadMessagesCount;
}

export async function fetchLabOrdersAwaitingApprovalOrReviewCount(
  medplum: MedplumClient,
  profile: ProfileResource,
  practiceId: string | undefined,
) {
  if (!profile) return 0;
  const profileReference = createReference(profile);

  const { bundle } = await medplum.searchResources(
    "ServiceRequest",
    {
      category: HealthGorillaNotifiableStatuses.join(","),
      "subject:Patient.general-practitioner": profileReference.reference,
      _compartment: getOrganizationReference(practiceId),
      _total: "accurate",
      _summary: "count",
    },
    { cache: "reload" },
  );

  return bundle.total ?? 0;
}
